import { useTranslation } from 'next-i18next'
import { ChangeEvent, useState } from 'react'

import SVGImage from '~/components/Image/SVGImage'

import statisticsAnalytics from '~/analytics/statistics/statisticsAnalytics'
import { ContainerHomGradientBorder } from '~/components/Containers'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ChevronDownIcon from '~/components/Icons/svgs/ChevronDownIcon'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'

import { StatisticsFilters } from '~/analytics/statistics/statisticsEvents.types'

import { Checkbox } from '~/components'

import {
  selectProductCardFilterOptions,
  selectProductCardTimeFilter,
  setProductCardFilterOptions,
  setProductCardsTimeFilter,
} from '../../redux/statistics.slice'
import { CardTimePeriodFilterOptions, ProductCardFilterOptions } from '../../types/profile-card.types'

export default function ProductCardTimeFilter(): React.ReactElement {
  const { t } = useTranslation('statistics')

  const dispatch = useAppDispatch()
  const productCardFilter = useAppSelector(selectProductCardTimeFilter)

  const onChangeOption = (e: ChangeEvent<HTMLSelectElement>): void => {
    const { value } = e.target

    statisticsAnalytics.logFilterChange(StatisticsFilters.PRODUCT_CARD_TIME, value)

    dispatch(setProductCardsTimeFilter(value))
  }

  const [hasFilter, setHasFilter] = useState(false)
  const selectedFilterOptions = useAppSelector(selectProductCardFilterOptions)
  const handleOnCheckboxChange = (filterOption: string): void => {
    const _selectedFilterOptions = selectedFilterOptions.includes(filterOption)
      ? selectedFilterOptions.filter((item) => item !== filterOption)
      : [...selectedFilterOptions, filterOption]

    dispatch(setProductCardFilterOptions(_selectedFilterOptions))
  }

  return (
    <>
      <div className="relative mb-4">
        <div className="flex items-center">
          <ContainerHomGradientBorder innerClassName="relative bg-white" outerClassName="w-full flex-item">
            <select
              className="w-full p-1 pl-3 font-semibold truncate bg-transparent appearance-none cursor-pointer focus:outline-0"
              onChange={onChangeOption}
            >
              {Object.values(CardTimePeriodFilterOptions).map((filterOption) => (
                <option key={filterOption} selected={filterOption === productCardFilter} value={filterOption}>
                  {t(filterOption)}
                </option>
              ))}
            </select>
            <div className="absolute top-1/2 right-4 w-5 h-5 transform -translate-y-1/2 pointer-events-none">
              <span className="h-full flex items-center justify-center rounded-full">
                <SVGIcon icon={ChevronDownIcon} pathClassName="text-black" size={18} />
              </span>
            </div>
          </ContainerHomGradientBorder>

          <div className="flex-item ml-auto">
            <SVGImage
              height={30}
              imageClassName="mr-2 ml-5 cursor-pointer"
              onClick={() => setHasFilter(!hasFilter)}
              srcKey="icons/filter.svg"
              width={35}
            />
          </div>
        </div>

        {hasFilter && (
          <div className="bg-gray-cool absolute right-0 top-0 w-[90%] flex flex-col justify-center items-start gap-2 pt-2.5 pr-3 pb-2.5 pl-3 rounded-4 z-50">
            {Object.values(ProductCardFilterOptions).map((filterOption) => (
              <div
                className="flex items-center text-white font-poppins text-base font-medium leading-5 tracking-[0.75px] w-full"
                key={filterOption}
              >
                <Checkbox
                  className="w-full"
                  id={`product-card-filter-${filterOption}`}
                  isBgWhite
                  isChecked={!!selectedFilterOptions.includes(filterOption)}
                  onChange={() => handleOnCheckboxChange(filterOption)}
                  text={t(filterOption)}
                  textPosition="left"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {hasFilter && (
        <div
          className="opacity-0 fixed top-0 left-0 h-full w-full bg-white z-40"
          onClick={() => setHasFilter(false)}
          role="button"
        />
      )}
    </>
  )
}
